import React, { useState } from 'react';
import Button from 'components/styleguide/button/Button';
import { getCSRFToken } from 'lib/request';

const styles = require('./eor_advisory.module.scss');

const iconCircleCheck = require('svg/icons-lj-styleguide-2025/circle-check.svg');
const iconExternalLink = require('svg/icons-lj-styleguide-2025/external-link.svg');
const iconClose = require('svg/icons-lj-styleguide-2025/x.svg');

interface EorAdvisoryProps {
  eorLearnMoreUrl?: string;
  dismissUrl?: string;
}

export default function EorAdvisory({
  eorLearnMoreUrl = 'https://landing.jobs/business-employer-of-record/',
  dismissUrl,
}: EorAdvisoryProps) {
  const [state, setState] = useState<'prompt' | 'confirmed' | 'hidden'>('prompt');

  const handleCalculate = () => {
    const onsite = document.getElementById('job_ad_form_remote_onsite_job') as HTMLInputElement | null;
    const partial = document.getElementById('job_ad_form_remote_partial_remote') as HTMLInputElement | null;
    const full = document.getElementById('job_ad_form_remote_full_remote') as HTMLInputElement | null;
    const global = document.getElementById('job_ad_form_remote_global_remote') as HTMLInputElement | null;
    let locationType = '';
    if (onsite?.checked) locationType = 'onsite';
    else if (partial?.checked) locationType = 'partial';
    else if (full?.checked) locationType = 'full';
    else if (global?.checked) locationType = 'global';
    document.dispatchEvent(new CustomEvent('eor-calculator:open', { detail: { locationType } }));
  };

  // Fire-and-forget: "Not now" shows an acknowledgement rather than hiding, so
  // waiting on the network would make the button feel dead, and a failed post
  // just leaves today's behaviour. Closing posts again, which retries.
  const persistDismissal = () => {
    if (!dismissUrl) return;

    // Accept: application/json is load-bearing — it is what exempts this from
    // CSRF verification. Failures are logged rather than surfaced: the user has
    // had their answer, and a lost dismissal is only today's bug returning.
    fetch(dismissUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-CSRF-Token': getCSRFToken() || '', Accept: 'application/json' },
    })
      .then(response => {
        // eslint-disable-next-line no-console
        if (!response.ok) console.error(`EOR advisory dismissal failed: ${response.status}`);
      })
      // eslint-disable-next-line no-console
      .catch(error => console.error('EOR advisory dismissal failed', error));
  };

  const handleDismiss = () => {
    persistDismissal();
    setState('confirmed');
  };

  const handleClose = () => {
    persistDismissal();
    setState('hidden');
  };

  if (state === 'hidden') return null;

  if (state === 'confirmed') {
    return (
      <div className={`${styles.advisory} ${styles.advisoryConfirmed}`}>
        <div className={styles.confirmedContent}>
          <img className={styles.checkIcon} src={iconCircleCheck} alt="" width="20" height="20" />
          <span className={styles.confirmedText}>Thank you for your answer!</span>
          <a className={styles.learnLink} href={eorLearnMoreUrl} target="_blank" rel="noopener noreferrer">
            Learn more about EOR
            <img className={styles.externalIcon} src={iconExternalLink} alt="" width="16" height="16" />
          </a>
        </div>
        <button className={styles.closeBtn} onClick={handleClose} aria-label="Dismiss">
          <img src={iconClose} alt="" width="20" height="20" />
        </button>
      </div>
    );
  }

  return (
    <div className={styles.advisory}>
      <p className={styles.text}>Hire Portuguese talent without opening a local entity. Want us to handle it?</p>
      <div className={styles.actions}>
        <Button context="neutral" type="primary" size="S" onClick={handleCalculate}>
          Yes, calculate costs
        </Button>
        <Button context="neutral" type="secondary" size="S" onClick={handleDismiss}>
          Not now
        </Button>
      </div>
    </div>
  );
}
